// src/pages/Inventory/StockOutModal.js
import React, {Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { inventoryService } from '../../services/api';
import { toast } from 'react-toastify';

const StockOutModal = ({ isOpen, onClose, onSuccess, partTypes, products, inventory }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPartType, setSelectedPartType] = useState(null);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [filteredPartTypes, setFilteredPartTypes] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [availableStock, setAvailableStock] = useState(0);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        setValue,
        setError,
        clearErrors,
    } = useForm();

    const watchPartTypeId = watch('part_type_id');
    const watchProductId = watch('product_id');
    const watchQuantity = watch('quantity');

    // Reset form when modal is opened or closed
    useEffect(() => {
        if (isOpen) {
            reset();
            setSelectedPartType(null);
            setSelectedProductId(null);
            setAvailableStock(0);

            // Filter part types to only those with stock
            const filtered = partTypes.filter(partType => {
                // For general inventory
                if (!partType.is_sku_specific) {
                    const inventoryItem = inventory.general_inventory.find(item =>
                                                                               item.part_type_id === partType.id
                    );
                    return inventoryItem && inventoryItem.current_stock > 0;
                }

                // For SKU-specific inventory
                const inventoryItems = inventory.sku_specific_inventory.filter(item =>
                                                                                   item.part_type_id === partType.id && item.current_stock > 0
                );
                return inventoryItems.length > 0;
            });

            setFilteredPartTypes(filtered);
        }
    }, [isOpen, reset, partTypes, inventory]);

    // Watch for part type changes to determine if it's SKU-specific
    useEffect(() => {
        if (watchPartTypeId) {
            const partType = partTypes.find(pt => pt.id === parseInt(watchPartTypeId));
            setSelectedPartType(partType);
            setSelectedProductId(null);
            setValue('product_id', '');
            setAvailableStock(0);

            // If switching from SKU-specific to non-SKU-specific, clear product_id
            if (partType && !partType.is_sku_specific) {
                setValue('product_id', '');

                // Find available stock for this part type in general inventory
                const inventoryItem = inventory.general_inventory.find(item =>
                                                                           item.part_type_id === partType.id
                );

                if (inventoryItem) {
                    setAvailableStock(inventoryItem.current_stock);
                }
            } else if (partType && partType.is_sku_specific) {
                // Filter products that have stock for this part type
                const productsWithStock = [];
                const productsWithInventory = new Set();

                inventory.sku_specific_inventory.forEach(item => {
                    if (item.part_type_id === partType.id && item.current_stock > 0) {
                        productsWithInventory.add(item.product_id);
                    }
                });

                products.forEach(product => {
                    if (productsWithInventory.has(product.id)) {
                        productsWithStock.push(product);
                    }
                });

                setFilteredProducts(productsWithStock);
            }
        } else {
            setSelectedPartType(null);
            setAvailableStock(0);
        }
    }, [watchPartTypeId, partTypes, setValue, inventory, products]);

    // Watch for product changes to update available stock
    useEffect(() => {
        if (selectedPartType && selectedPartType.is_sku_specific && watchProductId) {
            setSelectedProductId(parseInt(watchProductId));

            // Find available stock for this part type and product
            const inventoryItem = inventory.sku_specific_inventory.find(item =>
                                                                            item.part_type_id === selectedPartType.id &&
                                                                            item.product_id === parseInt(watchProductId)
            );

            if (inventoryItem) {
                setAvailableStock(inventoryItem.current_stock);
            } else {
                setAvailableStock(0);
            }
        }
    }, [watchProductId, selectedPartType, inventory]);

    // Validate quantity against available stock
    useEffect(() => {
        if (watchQuantity && parseInt(watchQuantity) > availableStock) {
            setError('quantity', {
                type: 'manual',
                message: `Cannot remove more than available stock (${availableStock})`
            });
        } else if (watchQuantity) {
            clearErrors('quantity');
        }
    }, [watchQuantity, availableStock, setError, clearErrors]);

    const onSubmit = async (data) => {
        // Validate one more time
        if (parseInt(data.quantity) > availableStock) {
            setError('quantity', {
                type: 'manual',
                message: `Cannot remove more than available stock (${availableStock})`
            });
            return;
        }

        setIsLoading(true);

        try {
            // Convert string IDs to numbers
            data.part_type_id = parseInt(data.part_type_id);
            data.quantity = parseInt(data.quantity);

            if (data.product_id) {
                data.product_id = parseInt(data.product_id);
            }

            await inventoryService.stockOut(data);
            toast.success('Stock removed successfully');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error removing stock:', error);
            toast.error(error.response?.data?.message || 'Failed to remove stock');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={onClose}>
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    {/* This element is to trick the browser into centering the modal contents. */}
                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                            <div className="absolute top-0 right-0 pt-4 pr-4">
                                <button
                                    type="button"
                                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                    onClick={onClose}
                                >
                                    <span className="sr-only">Close</span>
                                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>

                            <div>
                                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                                    <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                                        Stock Out
                                    </Dialog.Title>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            Remove inventory from your stock. Select a part type and enter the quantity to remove.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4">
                                <div>
                                    <label htmlFor="part_type_id" className="block text-sm font-medium text-gray-700">
                                        Part Type *
                                    </label>
                                    <select
                                        id="part_type_id"
                                        {...register('part_type_id', { required: 'Part type is required' })}
                                        className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md ${
                                            errors.part_type_id ? 'border-red-300' : ''
                                        }`}
                                    >
                                        <option value="">Select a part type</option>
                                        {filteredPartTypes.map((partType) => (
                                            <option key={partType.id} value={partType.id}>
                                                {partType.name} {partType.is_sku_specific ? '(SKU-specific)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.part_type_id && (
                                        <p className="mt-1 text-sm text-red-600">{errors.part_type_id.message}</p>
                                    )}
                                </div>

                                {selectedPartType && selectedPartType.is_sku_specific && (
                                    <div>
                                        <label htmlFor="product_id" className="block text-sm font-medium text-gray-700">
                                            Product SKU *
                                        </label>
                                        <select
                                            id="product_id"
                                            {...register('product_id', {
                                                required: selectedPartType?.is_sku_specific ? 'Product SKU is required for SKU-specific parts' : false
                                            })}
                                            className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md ${
                                                errors.product_id ? 'border-red-300' : ''
                                            }`}
                                        >
                                            <option value="">Select a product SKU</option>
                                            {filteredProducts.map((product) => (
                                                <option key={product.id} value={product.id}>
                                                    {product.sku}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.product_id && (
                                            <p className="mt-1 text-sm text-red-600">{errors.product_id.message}</p>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 flex justify-between">
                                        <span>Quantity *</span>
                                        {availableStock > 0 && (
                                            <span className="text-gray-500 text-xs">Available: {availableStock}</span>
                                        )}
                                    </label>
                                    <input
                                        type="number"
                                        id="quantity"
                                        {...register('quantity', {
                                            required: 'Quantity is required',
                                            min: { value: 1, message: 'Quantity must be at least 1' },
                                            max: { value: availableStock, message: `Cannot remove more than available stock (${availableStock})` }
                                        })}
                                        className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md ${
                                            errors.quantity ? 'border-red-300' : ''
                                        }`}
                                        placeholder="Enter quantity"
                                    />
                                    {errors.quantity && (
                                        <p className="mt-1 text-sm text-red-600">{errors.quantity.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="reference_number" className="block text-sm font-medium text-gray-700">
                                        Reference Number
                                    </label>
                                    <input
                                        type="text"
                                        id="reference_number"
                                        {...register('reference_number')}
                                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                                        placeholder="Order number, batch number, etc."
                                    />
                                </div>

                                <div>
                                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                                        Notes
                                    </label>
                                    <textarea
                                        id="notes"
                                        rows={3}
                                        {...register('notes')}
                                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                                        placeholder="Add any notes about this stock out transaction"
                                    />
                                </div>

                                {/* Available stock warning if quantity is too high */}
                                {watchQuantity && parseInt(watchQuantity) > availableStock && (
                                    <div className="rounded-md bg-red-50 p-4">
                                        <div className="flex">
                                            <div className="flex-shrink-0">
                                                <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                                            </div>
                                            <div className="ml-3">
                                                <h3 className="text-sm font-medium text-red-800">
                                                    Insufficient stock available
                                                </h3>
                                                <div className="mt-2 text-sm text-red-700">
                                                    <p>
                                                        You cannot remove more than the available stock ({availableStock}).
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                    <button
                                        type="submit"
                                        disabled={isLoading || !availableStock || (watchQuantity && parseInt(watchQuantity) > availableStock)}
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? 'Processing...' : 'Remove Stock'}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default StockOutModal;
